import { toast } from 'react-toastify';
import * as api from '../../api/auth.api';
import * as actionType from '../constants/auth';

export const loginUser = (data) => async (dispatch) => {
  try {
    dispatch({ type: actionType.USER_LOADING });
    const response = await api.signIn(data);

    if (response.ok) {
      toast.success('You have successfully login');
      const { user, authorization, refreshToken } = response.data.data;
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
      dispatch({ type: actionType.LOGIN_SUCCEED, payload: { token: authorization.token, user } });
      return { success: true };
    }

    const errorMessage = response.data ? response.data.message || 'Invalid username or password' : response.problem;
    toast.error(errorMessage);
    dispatch({ type: actionType.AUTH_ERROR });
    return {
      success: false,
      captchaRequired: response.data?.data?.captchaRequired || (response.data?.data?.loginAttempts >= 3),
      loginAttempts: response.data?.data?.loginAttempts || 0
    };
  } catch (error) {
    console.log(error);
    toast.error('Something went wrong');
    dispatch({ type: actionType.AUTH_ERROR });
    return { success: false };
  }
};

export const loginWithGoogle = (accessToken, appId) => async (dispatch) => {
  try {
    dispatch({ type: actionType.USER_LOADING });
    const response = await api.googleSignIn({ accessToken, provider: 'google', appId });

    if (response.ok) {
      toast.success('You have successfully logged in with Google');
      const { user, authorization, refreshToken } = response.data.data;
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
      dispatch({ type: actionType.LOGIN_SUCCEED, payload: { token: authorization.token, user } });
      return { success: true };
    }

    const errorMessage = response.data ? response.data.message || 'Google authentication failed' : response.problem;
    toast.error(errorMessage);
    dispatch({ type: actionType.AUTH_ERROR });
    return { success: false };
  } catch (error) {
    console.log(error);
    toast.error('Something went wrong during Google Login');
    dispatch({ type: actionType.AUTH_ERROR });
    return { success: false };
  }
};

export const sendOTP = (email) => async (dispatch) => {
  try {
    const response = await api.sendAuthOtp({ email });
    if (response.ok) {
      toast.success(response.data.message || 'OTP sent successfully to your email');
      return true;
    }
    const errorMessage = response.data ? response.data.message || 'Failed to send OTP' : response.problem;
    toast.error(errorMessage);
    return false;
  } catch (error) {
    console.log(error);
    toast.error('Something went wrong while sending OTP');
    return false;
  }
};

export const loginWithOTP = (data) => async (dispatch) => {
  try {
    dispatch({ type: actionType.USER_LOADING });
    const response = await api.verifyAuthOtp(data);

    if (response.ok) {
      toast.success('You have successfully logged in');
      const { user, authorization, refreshToken } = response.data.data;
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      }
      dispatch({ type: actionType.LOGIN_SUCCEED, payload: { token: authorization.token, user } });
      return { success: true };
    }

    const errorMessage = response.data ? response.data.message || 'Invalid OTP' : response.problem;
    toast.error(errorMessage);
    dispatch({ type: actionType.AUTH_ERROR });
    return {
      success: false,
      captchaRequired: response.data?.data?.captchaRequired || (response.data?.data?.loginAttempts >= 3),
      loginAttempts: response.data?.data?.loginAttempts || 0
    };
  } catch (error) {
    console.log(error);
    toast.error('Something went wrong during OTP verification');
    dispatch({ type: actionType.AUTH_ERROR });
    return { success: false };
  }
};

export const restoreUserSession = () => async (dispatch) => {
  dispatch({ type: actionType.USER_LOADING });
  const userSession = localStorage.getItem('user_session') ? JSON.parse(localStorage.getItem('user_session')) : {};

  if (!userSession.token) {
    dispatch({ type: actionType.AUTH_ERROR });
    window.location.href = '/login';
    return;
  }

  const response = await api.getUser(userSession.token);

  if (response.status === 403) {
    toast.error('Your access to this application has been revoked.');
    const bootstrappedApp = sessionStorage.getItem('bootstrappedApp');
    if (bootstrappedApp) {
      // Bootstrapped app (tenant site) -> Log out completely
      dispatch(userLogOut());
      window.location.href = '/login';
    } else {
      // Main site -> Return to the main page where all apps are listed
      localStorage.removeItem('activeApp');
      localStorage.removeItem('cached_dashboard_overview');
      dispatch({ type: actionType.CLEAR_APP_DATA });
      dispatch({ type: actionType.SET_ACTIVE_APP, payload: null });
      window.location.href = '/';
    }
    return;
  }

  if (response.status === 401 || response.status === 409) {
    dispatch({ type: actionType.AUTH_ERROR });
    return;
  }

  if (response.ok) {
    dispatch({ type: actionType.USER_LOADED, payload: { token: userSession.token, user: response.data.data } });
  } else {
    dispatch({ type: actionType.AUTH_ERROR });
  }
};

export const clearAppData = () => (dispatch) => {
  try {
    localStorage.removeItem('cached_dashboard_overview');
  } catch (e) {
    // Ignore storage errors
  }
  dispatch({ type: actionType.CLEAR_APP_DATA });
};

export const userLogOut = () => (dispatch) => {
  localStorage.removeItem('user_session');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('activeApp');
  localStorage.removeItem('cached_dashboard_overview');
  dispatch({ type: actionType.CLEAR_APP_DATA });
  dispatch({ type: actionType.USER_LOGOUT });
};


export const verifyResetOTP = (email, otp) => async (dispatch) => {
  try {
    const response = await api.verifyResetOtp({ email, otp });
    if (response.ok) {
      toast.success(response.data.message || 'OTP verified successfully');
      return true;
    }
    const errorMessage = response.data ? response.data.message || 'Invalid OTP' : response.problem;
    toast.error(errorMessage);
    return false;
  } catch (error) {
    console.log(error);
    toast.error('Something went wrong during OTP verification');
    return false;
  }
};

export const resetPassword = (email, otp, password) => async (dispatch) => {
  try {
    const response = await api.resetPassword({ email, otp, password });
    if (response.ok) {
      toast.success(response.data.message || 'Password reset successfully. You can now login.');
      return true;
    }
    const errorMessage = response.data ? response.data.message || 'Failed to reset password' : response.problem;
    toast.error(errorMessage);
    return false;
  } catch (error) {
    console.log(error);
    toast.error('Something went wrong during password reset');
    return false;
  }
};
